import React, { useState } from 'react';
import { DatabaseIcon, HistoryIcon, InfoIcon, SaveIcon, ScaleIcon } from 'lucide-react';
import { BLCMasterData as BLCMasterDataType, LookupTable, TableId } from '../../data/blcTables';
import { LookupTableEditor } from './LookupTableEditor';
type SubTab = 'le' | 'bav' | 'dis' | 'antilog' | 'fee' | 'maund';
const subTabs: {
  id: SubTab;
  label: string;
}[] = [{
  id: 'le',
  label: 'Le Tables'
}, {
  id: 'bav',
  label: 'Bav Tables'
}, {
  id: 'dis',
  label: 'Displacement Tables'
}, {
  id: 'antilog',
  label: 'Antilog (Load) Table'
}, {
  id: 'fee',
  label: 'Fee Configuration'
}, {
  id: 'maund',
  label: 'Maund Conversion'
}];
interface BLCMasterDataProps {
  master: BLCMasterDataType;
  onChange: (master: BLCMasterDataType) => void;
}
export function BLCMasterData({
  master,
  onChange
}: BLCMasterDataProps) {
  const [subTab, setSubTab] = useState<SubTab>('le');
  const [feeDraft, setFeeDraft] = useState({
    unitKg: String(master.feeConfig.unitKg),
    ratePerUnit: String(master.feeConfig.ratePerUnit),
    effectiveDate: master.feeConfig.effectiveDate
  });
  const [maundDraft, setMaundDraft] = useState(String(master.maundFactor));
  const updateTable = (table: LookupTable): void => {
    onChange({
      ...master,
      tables: {
        ...master.tables,
        [table.id as TableId]: table
      }
    });
  };
  const saveFee = (): void => {
    const unitKg = parseFloat(feeDraft.unitKg);
    const ratePerUnit = parseFloat(feeDraft.ratePerUnit);
    if (!Number.isFinite(unitKg) || unitKg <= 0) return;
    if (!Number.isFinite(ratePerUnit) || ratePerUnit < 0) return;
    onChange({
      ...master,
      feeConfig: {
        unitKg,
        ratePerUnit,
        effectiveDate: feeDraft.effectiveDate
      },
      feeHistory: [{
        effectiveDate: feeDraft.effectiveDate,
        unitKg,
        ratePerUnit,
        approvedBy: 'Pending sign-off (this session)'
      }, ...master.feeHistory]
    });
  };
  const saveMaund = (): void => {
    const factor = parseFloat(maundDraft);
    if (!Number.isFinite(factor) || factor <= 0) return;
    onChange({
      ...master,
      maundFactor: factor,
      maundFactorUpdatedBy: 'You (current session)',
      maundFactorUpdatedAt: new Date().toISOString().slice(0, 16).replace('T', ' ')
    });
  };
  return <div className="space-y-5" data-id="element-46" data-mp="3os7eufl9pi3w">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-47" data-mp="3tqin4lvwzkk0">
        <InfoIcon size={18} className="text-amber-700 flex-shrink-0 mt-0.5" data-id="element-48" data-mp="3fqq2n8aze0kh" />
        <p className="text-sm text-amber-900" data-id="element-49" data-mp="3fsu3tihhwlh2">
          These tables and rates legally define boat load capacity and licence
          fees. Grids are locked by default — unlock a grid to edit, and record
          the approval memo before changes are relied upon in the field.{' '}
          <strong data-id="element-50" data-mp="29byvimv9lji0">Type A and Type B</strong> are maintained separately but are
          seeded with the manual's shared Type A/B values, so they produce
          identical results until one of them is edited.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-id="element-51" data-mp="2art41ua6vu5v">
        {subTabs.map(tab => <button key={tab.id} onClick={() => setSubTab(tab.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${subTab === tab.id ? 'bg-[#1B5E20] border-[#1B5E20] text-white' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-52" data-mp="2apqfzfslkb3s">
            {tab.label}
          </button>)}
      </div>

      {subTab === 'le' && <div className="space-y-5" data-id="element-53" data-mp="1n0nsvsf71q1w">
          <LookupTableEditor table={master.tables.Le_TypeA} onChange={updateTable} data-id="element-54" data-mp="zco2wuppi3od" />
          <LookupTableEditor table={master.tables.Le_TypeB} onChange={updateTable} data-id="element-55" data-mp="ue2zynj2l5w8" />
          <LookupTableEditor table={master.tables.Le_TypeC} onChange={updateTable} data-id="element-56" data-mp="3tsn1v6s2fk5e" />
        </div>}

      {subTab === 'bav' && <div className="space-y-5" data-id="element-57" data-mp="6n4f4iasbeh7">
          <LookupTableEditor table={master.tables.Bav_TypeA} onChange={updateTable} data-id="element-58" data-mp="6m2t5is8d7kp" />
          <LookupTableEditor table={master.tables.Bav_TypeB} onChange={updateTable} data-id="element-59" data-mp="bevwq3byq9v7" />
          <LookupTableEditor table={master.tables.Bav_TypeC} onChange={updateTable} data-id="element-60" data-mp="gf1rvthfyei1" />
        </div>}

      {subTab === 'dis' && <div className="space-y-5" data-id="element-61" data-mp="qbd7ascbvkgp">
          <LookupTableEditor table={master.tables.Dis_TypeA} onChange={updateTable} data-id="element-62" data-mp="ucgl79atf6fa" />
          <LookupTableEditor table={master.tables.Dis_TypeB} onChange={updateTable} data-id="element-63" data-mp="2s2q8p7f37a80" />
          <LookupTableEditor table={master.tables.Dis_TypeC} onChange={updateTable} data-id="element-64" data-mp="2w4v162tsu0t2" />
        </div>}

      {subTab === 'antilog' && <LookupTableEditor table={master.tables.Antilog_Kg} onChange={updateTable} data-id="element-65" data-mp="2jt2dtbip14yo" />}

      {subTab === 'fee' && <div className="grid gap-5 lg:grid-cols-2" data-id="element-66" data-mp="2eur55444x0i1">
          <section className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-67" data-mp="2atp3kgmqwccw">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-68" data-mp="37iwrtwer0zki">
              <DatabaseIcon size={18} className="text-[#1B5E20]" data-id="element-69" data-mp="37gsobfkri2kr" />
              Fee Configuration
            </h3>
            <p className="text-sm text-gray-600 mt-1" data-id="element-70" data-mp="2yi8cmydmct61">
              Annual Fee = ROUNDUP(Load Kg ÷ Unit size) × Rate per unit.
            </p>
            <div className="mt-4 space-y-4" data-id="element-71" data-mp="2tilzsq1pdexr">
              <div data-id="element-72" data-mp="1n3bkhfm51nug">
                <label htmlFor="unit-kg" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-73" data-mp="1n3utg6tt91wc">
                  Unit size (Kg per unit)
                </label>
                <input id="unit-kg" type="number" value={feeDraft.unitKg} onChange={e => setFeeDraft({
              ...feeDraft,
              unitKg: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20] focus:outline-none" data-id="element-74" data-mp="1jmfief811v4a" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-75" data-mp="15lcv5z5aqers">
                  Default 1,000 Kg (10 quintal = 1 unit).
                </p>
              </div>
              <div data-id="element-76" data-mp="qyjkx8amvz6g">
                <label htmlFor="rate-per-unit" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-77" data-mp="quvs6bd0jgss">
                  Rate per unit (Tk)
                </label>
                <input id="rate-per-unit" type="number" value={feeDraft.ratePerUnit} onChange={e => setFeeDraft({
              ...feeDraft,
              ratePerUnit: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20] focus:outline-none" data-id="element-78" data-mp="muve4rttt7m9" />
              </div>
              <div data-id="element-79" data-mp="m7nw0kajj5f7">
                <label htmlFor="effective-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-80" data-mp="m75w9doo692j">
                  Effective date
                </label>
                <input id="effective-date" type="date" value={feeDraft.effectiveDate} onChange={e => setFeeDraft({
              ...feeDraft,
              effectiveDate: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20] focus:outline-none" data-id="element-81" data-mp="vxhapu5zc0no" />
              </div>
              <button type="button" onClick={saveFee} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium" data-id="element-82" data-mp="2c9bcs6v6owed">
                <SaveIcon size={16} data-id="element-83" data-mp="1oljvspyp7v6r" />
                Save fee rate
              </button>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-5" data-id="element-84" data-mp="1smdp5yuwu271">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-85" data-mp="1xmi1rdsp6crz">
              <HistoryIcon size={18} className="text-[#1B5E20]" data-id="element-86" data-mp="22cyhj4jzqahi" />
              Fee Rate History
            </h3>
            <table className="w-full mt-4 text-sm" data-id="element-87" data-mp="14xuc760x118m">
              <thead data-id="element-88" data-mp="14x1d8r877g11">
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-89" data-mp="14vqbewybcick">
                  <th className="py-2" data-id="element-90" data-mp="12do9f1o1fgtn">Effective</th>
                  <th className="py-2" data-id="element-91" data-mp="12cd4gyiuw30y">Unit (Kg)</th>
                  <th className="py-2" data-id="element-92" data-mp="xds5f2fcbpe3">Rate (Tk)</th>
                  <th className="py-2" data-id="element-93" data-mp="xbyv9d5iqn1c">Approved by</th>
                </tr>
              </thead>
              <tbody data-id="element-94" data-mp="sh1rolr0d5vu">
                {master.feeHistory.map((entry, idx) => <tr key={`${entry.effectiveDate}-${idx}`} className="border-b border-gray-100 last:border-0" data-id="element-95" data-mp="octxi91s6xq8" data-mpi={idx}>
                    <td className="py-2 font-medium text-gray-900" data-id="element-96" data-mp="enaixfl3pq16">
                      {entry.effectiveDate}
                    </td>
                    <td className="py-2 text-gray-700" data-id="element-97" data-mp="9oqsb1vkbq2h">{entry.unitKg}</td>
                    <td className="py-2 text-gray-700" data-id="element-98" data-mp="4qp9mi21tsrj">৳ {entry.ratePerUnit}</td>
                    <td className="py-2 text-gray-500 text-xs" data-id="element-99" data-mp="o2fgahwnvr6">
                      {entry.approvedBy}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </section>
        </div>}

      {subTab === 'maund' && <section className="bg-white border border-gray-200 rounded-lg p-5 max-w-xl" data-id="element-100" data-mp="2szpormsn8271">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2" data-id="element-101" data-mp="37oc9k411hm5n">
            <ScaleIcon size={18} className="text-[#1B5E20]" data-id="element-102" data-mp="37qewugat516o" />
            Maund Conversion Factor
          </h3>
          <p className="text-sm text-gray-600 mt-1" data-id="element-103" data-mp="2ezol5zkzugnv">
            Load (Maund) = ROUND(Load Kg ÷ factor, 0).
          </p>
          <div className="mt-4 flex items-end gap-3" data-id="element-104" data-mp="2a13jdvq3foxb">
            <div className="flex-1" data-id="element-105" data-mp="2a2epw3axbas4">
              <label htmlFor="maund-factor" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-106" data-mp="2oxkr8cz2qlf4">
                Kg per maund
              </label>
              <input id="maund-factor" type="number" step="0.0001" value={maundDraft} onChange={e => setMaundDraft(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B5E20] focus:outline-none" data-id="element-107" data-mp="47deyzzz2fcx" />
            </div>
            <button type="button" onClick={saveMaund} className="flex items-center gap-2 px-4 py-2 bg-[#1B5E20] text-white rounded-lg hover:bg-[#2E7D32] font-medium" data-id="element-108" data-mp="bos29blh0fwb">
              <SaveIcon size={16} data-id="element-109" data-mp="pi15svmqm76x" />
              Save
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5" data-id="element-110" data-mp="uhcnc6uxhgpk">
            <HistoryIcon size={14} data-id="element-111" data-mp="ujgpaj99o5n7" />
            Last updated by{' '}
            <span className="font-medium text-gray-700" data-id="element-112" data-mp="gpppi2lc804o">
              {master.maundFactorUpdatedBy}
            </span>{' '}
            on{' '}
            <span className="font-medium text-gray-700" data-id="element-113" data-mp="18hnhpjvg5dfh">
              {master.maundFactorUpdatedAt}
            </span>
          </p>
        </section>}
    </div>;
}